import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '../..';
import { GradeSchoolClass, SchoolClass, SchoolClassSelection } from '@/models/school-class/school-class';
import { Student } from '@/models/student/student';
import { GEnum } from '@/constants/enum.defination';
import ExamSelectionType = GEnum.ExamineeSelectionType;
import { Competency, CompetencySelection } from '@/models/competency/competency';
import { studentService } from '@/service/api/student.service';
import { jgrowEventService } from '@/service/api/jgrow-event.service';
import { Event } from '@/models/jgrow/event';
import { EventParam, EventCreateRequest } from '@/models/jgrow/event-param';
import { Activity, ActivityItem } from '@/models/activity/activity';
import moment from 'moment';

export interface IJgrowEventCreateState {
  currentStep: number; // 現在の登録ステップ
  mode: number; // 更新モード(1:登録,2:編集,3:コピー)
  examineeSelectionType: ExamSelectionType; // 受検対象者タイプ(1:クラス,2:生徒)
  selectionSchoolClasses: SchoolClassSelection[]; // チェックボックスグループで選択されたクラス一覧
  examineeStudents: Student[]; // 選択された学生一覧
  selectionCognitive: CompetencySelection; // コンピテンシータイプ「認知」のチェックボックスグループ
  selectionSelf: CompetencySelection; // コンピテンシータイプ「自己」のチェックボックスグループ
  selectionGift: CompetencySelection; // コンピテンシータイプ「他者」のチェックボックスグループ
  selectionCommunity: CompetencySelection; // コンピテンシータイプ「コミュニティ」のチェックボックスグループ
  visitConfirmView: boolean; // 確認画面到達フラグ
  visitQuestionSettingView: boolean; // 確認画面到達フラグ
  visitExamineeSettingView: boolean; // 受検者設定画面到達フラグ
  examineeSchoolClassIDs: number[]; // 受検クラスIDリスト
  examineeStudentIDs: number[]; // 受検生徒IDリスト
  alreadyAssignedStudentIDs: Set<number>; // すでに受検者として割り当てられている生徒
  isAutoPageTransition: boolean; // 受検設定登録または更新後の自動遷移確認フラグ
  params: EventParam; // イベント登録用のデータまとめ
  selectActivities: Activity[]; // アクティビティの選択
  selectionActivities: Activity[]; // アクティビティの選択候補（検索結果で表示するもの）
  selectedActivities: Activity[]; // 選択したアクティビティ
  createdEvent: Event; // 作成済みイベントを保持（更新時に取得したもの）
}
@Module({ dynamic: true, store, name: 'jgrowEventCreateModule', namespaced: true })
class JgrowEventCreateModule extends VuexModule implements IJgrowEventCreateState {
  currentStep: number = 0;
  mode: number = 0;
  examineeSelectionType: ExamSelectionType = GEnum.ExamineeSelectionType.Class;
  selectionSchoolClasses: SchoolClassSelection[] = [];
  examineeStudents: Student[] = [];
  selectionCognitive: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Cognitive);
  selectionSelf: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Self);
  selectionGift: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Gift);
  selectionCommunity: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Community);
  visitConfirmView: boolean = false;
  visitQuestionSettingView: boolean = false;
  visitExamineeSettingView: boolean = false;
  examineeSchoolClassIDs: number[] = [];
  examineeStudentIDs: number[] = [];
  alreadyAssignedStudentIDs: Set<number> = new Set<number>();
  isAutoPageTransition: boolean = false;
  params: EventParam = new EventParam();
  selectActivities: Activity[] = [];
  selectionActivities: Activity[] = [];
  selectedActivities: Activity[] = [];
  createdEvent = new Event();

  /**
   * 受検編集時に追加登録される生徒一覧(ただし、受検作成時はalreadyAssignedStudentIDsが空のため、assignedExamineeStudentsと同等の結果が得られる)
   */
  get plusExamineeStudents(): Student[] {
    return this.assignedExamineeStudents.filter((student: Student) => !jgrowEventCreateModule.alreadyAssignedStudentIDs.has(student.studentID));
  }

  /**
   * 受検登録時に登録される生徒一覧
   */
  get assignedExamineeStudents(): Student[] {
    return this.examineeStudents.filter((student: Student) => !student.selected);
  }

  get examineeSelectionTypeString(): string {
    switch (this.examineeSelectionType) {
      case GEnum.ExamineeSelectionType.Class:
        return 'クラス別';
      case GEnum.ExamineeSelectionType.Student:
        return '生徒個別';
    }
  }

  // JGROW イベントにおけるデフォルト設定されているコンピテンシー
  get jgrowEventBasicCompetencyIds(): number[] {
    return [
      GEnum.Competency.ID1,
      GEnum.Competency.ID2,
      GEnum.Competency.ID3,
      GEnum.Competency.ID4,
      GEnum.Competency.ID5,
      GEnum.Competency.ID6,
      GEnum.Competency.ID9,
      GEnum.Competency.ID12,
      GEnum.Competency.ID13,
      GEnum.Competency.ID14,
      GEnum.Competency.ID15,
      GEnum.Competency.ID16,
      GEnum.Competency.ID17,
      GEnum.Competency.ID18,
      GEnum.Competency.ID19,
      GEnum.Competency.ID20,
      GEnum.Competency.ID22,
      GEnum.Competency.ID23,
    ];
  }

  get jgrowEventPageModeCreate(): boolean {
    return this.mode === GEnum.JgrowEventPageMode.Create;
  }

  get jgrowEventPageModeEdit(): boolean {
    return this.mode === GEnum.JgrowEventPageMode.Edit;
  }

  get jgrowEventPageModeDetail(): boolean {
    return this.mode === GEnum.JgrowEventPageMode.Detail;
  }

  get isAssignStudent(): boolean {
    return this.examineeSelectionType === GEnum.ExamineeSelectionType.Student;
  }

  get isAssignSchoolClass(): boolean {
    return this.examineeSelectionType === GEnum.ExamineeSelectionType.Class;
  }

  get selectedSchoolClassIDs(): number[] {
    return this.selectionSchoolClasses.map((selection: SchoolClassSelection) => selection.checkAllGroup).flat();
  }

  get selectedCompetencyIDs(): number[] {
    return [this.selectionCognitive.checkAllGroup, this.selectionSelf.checkAllGroup, this.selectionGift.checkAllGroup, this.selectionCommunity.checkAllGroup].flat();
  }

  get createdEventData(): Event {
    return this.createdEvent;
  }

  get selectedActivityIDs(): number[] {
    let ids: number[] = [];
    this.selectedActivities.forEach((act: Activity) => { 
      ids.push(act.activityID);
    });
    return ids;
  }

  // 追加設問が1つでも存在する場合
  get hasAdditionalQuestion(): boolean {
    return this.params.q1Enable || this.params.q2Enable || this.params.q3Enable;
  }

  @Mutation
  clean() {
    this.examineeSelectionType = GEnum.ExamineeSelectionType.Class;
    this.examineeStudents = [];
    this.visitConfirmView = false;
    this.visitQuestionSettingView = false;
    this.visitExamineeSettingView = false;
    this.examineeSchoolClassIDs = [];
    this.examineeStudentIDs = [];
    this.alreadyAssignedStudentIDs = new Set<number>();
    this.currentStep = 0;
    this.isAutoPageTransition = false;
    this.params = new EventParam();
    this.selectActivities = [];
    this.selectionActivities = [];
    this.selectedActivities = [];
    this.createdEvent = new Event();
  }

  @Mutation
  setCreatedEvent(event: Event) {
    this.createdEvent = event;
  }  

  @Mutation
  setEventParam() {
    this.params = new EventParam().convertFromEvent(this.createdEvent);
  }  

  @Mutation
  setAlreadyAssignedStudentIDs(students: Student[]) {
    this.alreadyAssignedStudentIDs = students.reduce((result: Set<number>, student: Student) => {
      result.add(student.studentID);
      return result;
    }, new Set<number>());
  }

  @Mutation
  setVisitConfirmView(value: boolean) {
    this.visitConfirmView = value;
  }

  @Mutation
  setVisitQuestionSettingView(value: boolean) {
    this.visitQuestionSettingView = value;
  }

  @Mutation
  setVisitExamineeSettingView(value: boolean) {
    this.visitExamineeSettingView = value;
  }

  @Mutation
  setIsAutoPageTransition(value: boolean) {
    this.isAutoPageTransition = value;
  }

  /**
   * コンピテンシー用のcheckbox groupにラベル設定し、初期化する
   */
  @Mutation
  cleanCompetencySelections() {
    this.selectionCognitive = new CompetencySelection(GEnum.CompetencyType.Cognitive);
    this.selectionSelf = new CompetencySelection(GEnum.CompetencyType.Self);
    this.selectionGift = new CompetencySelection(GEnum.CompetencyType.Gift);
    this.selectionCommunity = new CompetencySelection(GEnum.CompetencyType.Community);
  }

  /**
   * クラス用のcheckbox groupにチェック値を反映する
   * @param schoolClasses 選択されたクラス
   */
  @Mutation
  setSchoolClassSelection(schoolClasses: SchoolClass[]) {
    this.selectionSchoolClasses.forEach((selectionSchoolClass: SchoolClassSelection) => {
      selectionSchoolClass.setCheckAllGroup(schoolClasses.filter((schoolClass: SchoolClass) => selectionSchoolClass.grade === schoolClass.grade));
    });
  }

  /**
   * コンピテンシー用のcheckbox groupにチェック値を反映する
   * @param competencies 選択されたコンピテンシー
   */
  @Mutation
  setCompetencySelections(competencies: Competency[]) {
    this.selectionCognitive.setCheckGroup(
      competencies.filter((competency: Competency) => competency.competencyType === GEnum.CompetencyType.Cognitive).map((competency: Competency) => competency.competencyID)
    );
    this.selectionSelf.setCheckGroup(competencies.filter((competency: Competency) => competency.competencyType === GEnum.CompetencyType.Self).map((competency: Competency) => competency.competencyID));
    this.selectionGift.setCheckGroup(competencies.filter((competency: Competency) => competency.competencyType === GEnum.CompetencyType.Gift).map((competency: Competency) => competency.competencyID));
    this.selectionCommunity.setCheckGroup(
      competencies.filter((competency: Competency) => competency.competencyType === GEnum.CompetencyType.Community).map((competency: Competency) => competency.competencyID)
    );
  }

  @Mutation
  setExamineeSelectionType(value: GEnum.ExamineeSelectionType) {
    this.examineeSelectionType = value;
  }

  @Mutation
  setSelectionCognitive(value: CompetencySelection) {
    this.selectionCognitive = value;
  }
  @Mutation
  setSelectionSelf(value: CompetencySelection) {
    this.selectionSelf = value;
  }
  @Mutation
  setSelectionGift(value: CompetencySelection) {
    this.selectionGift = value;
  }
  @Mutation
  setSelectionCommunity(value: CompetencySelection) {
    this.selectionCommunity = value;
  }

  @Mutation
  setMode(value: number) {
    this.mode = value;
  }

  @Mutation
  setExamnieeSelectionType(value: GEnum.ExamineeSelectionType) {
    this.examineeSelectionType = value;
  }

  @Mutation
  setExamineeStudents(value: Student[]) {
    this.examineeStudents = value;
  }

  @Mutation
  toggleSelectForStudents(index: number) {
    this.examineeStudents[index].selected = !this.examineeStudents[index].selected;
  }

  @Mutation
  setCurrentStep(value: number) {
    this.currentStep = value;
  }

  @Mutation
  setExamineeStudentIDs(value: number[]) {
    this.examineeStudentIDs = value;
  }

  @Mutation
  setExamineeSchoolClassIDs(value: number[]) {
    this.examineeSchoolClassIDs = value;
  }

  @Mutation
  resetSchoolClassSelection(gradeSchoolClasses: GradeSchoolClass[]) {
    this.selectionSchoolClasses = [];
    gradeSchoolClasses.forEach((v: GradeSchoolClass, _: number) => {
      this.selectionSchoolClasses.push(new SchoolClassSelection(v.grade));
    });
  }

  @Mutation
  mergeExamineeStudents(newStudents: Student[]) {
    let tempExamineeStudents: Set<number> = this.examineeStudents.reduce((result: Set<number>, student: Student) => {
      result.add(student.studentID);
      return result;
    }, new Set<number>());
    newStudents.forEach((student: Student) => {
      if (!tempExamineeStudents.has(student.studentID)) {
        this.examineeStudents.push(student);
      }
    });
  }

  @Mutation
  cleanSelectedStudents() {
    this.examineeStudents = [];
  }

  @Mutation
  setActivitySelections(values: Activity[]) {
    this.selectActivities = values;
  }

  @Mutation
  setActivitySelected(values: Activity[]) {
    this.selectedActivities = values;
  }

  @Mutation
  choiceActivity(act: Activity) {
    const exist = this.selectedActivities.some((activ: Activity) =>  act.activityID === activ.activityID);
    if(this.selectedActivities.length < 5 && !exist){
      this.selectedActivities.push(act);
    }
  }

  @Mutation
  discardActivity(act: Activity) {
    this.selectedActivities = this.selectedActivities.filter((activ: Activity) => {
      if (act.activityID !== activ.activityID) return activ;
    });
  }

  @Mutation
  doActivitySearchName(name: string) {
    this.selectionActivities = this.selectActivities.filter((act: Activity) => {
      if (act.activityName.includes(name)) return act;
    });
  }

  @Mutation
  doActivitySearchItem(name: string) {
    this.selectionActivities = this.selectActivities.filter((act: Activity) => {
      if (act.activityItems.find((item: ActivityItem) => item.activityItemName.includes(name))) return act;
    });
  }

  /**
   * 受検設定更新
   * @param updateEvalSettingDetail 受検設定更新情報
   */
  @Action({ rawError: true })
  async updateEvent() {
    const request: EventCreateRequest = {
      eventName: this.params.title,
      startDate: moment(this.params.startDate).toISOString(),
      endDate: moment(this.params.endDate).toISOString(),
      destination: this.params.destination,
      q1Enable: this.params.q1Enable,
      q1: this.params.q1,
      q2Enable: this.params.q2Enable,
      q2: this.params.q2,
      q3Enable: this.params.q3Enable,
      q3: this.params.q3,
      activities: jgrowEventCreateModule.selectedActivityIDs,
      competencies: this.selectedCompetencyIDs,
      schoolClassIDs: this.examineeSchoolClassIDs,
      userIDs: this.examineeStudentIDs,
      beforeSurveyStartDate: moment(this.params.beforeSurveyStartDate).toISOString(),
      beforeSurveyEndDate: moment(this.params.beforeSurveyEndDate).toISOString(),
      afterSurveyStartDate: moment(this.params.afterSurveyStartDate).toISOString(),
      afterSurveyEndDate: moment(this.params.afterSurveyEndDate).toISOString(),
      beforeExam: false,
      beforeExamStartDate: null,
      beforeExamEndDate: null,
      afterExam: false,
      afterExamStartDate: null,
      afterExamEndDate: null,
    };

    await jgrowEventService.updateEvent(this.createdEventData.ID, request);

    return true;
  }

  /**
   * JGROW - イベント登録
   */
  @Action({ rawError: true })
  async createEvent() {
    const request: EventCreateRequest = {
      eventName: this.params.title,
      startDate: moment(this.params.startDate).toISOString(),
      endDate: moment(this.params.endDate).toISOString(),
      destination: this.params.destination,
      q1Enable: this.params.q1Enable,
      q1: this.params.q1,
      q2Enable: this.params.q2Enable,
      q2: this.params.q2,
      q3Enable: this.params.q3Enable,
      q3: this.params.q3,
      activities: jgrowEventCreateModule.selectedActivityIDs,
      competencies: this.selectedCompetencyIDs,
      schoolClassIDs: this.examineeSchoolClassIDs,
      userIDs: this.examineeStudentIDs,
      beforeSurveyStartDate: moment(this.params.beforeSurveyStartDate).toISOString(),
      beforeSurveyEndDate: moment(this.params.beforeSurveyEndDate).toISOString(),
      afterSurveyStartDate: moment(this.params.afterSurveyStartDate).toISOString(),
      afterSurveyEndDate: moment(this.params.afterSurveyEndDate).toISOString(),
      beforeExam: false,
      beforeExamStartDate: null,
      beforeExamEndDate: null,
      afterExam: false,
      afterExamStartDate: null,
      afterExamEndDate: null,
    };

    // イベント前受検が設定されている場合
    if (this.params.beforeExam) {
      request.beforeExam = this.params.beforeExam;
      request.beforeExamStartDate = moment(this.params.beforeExamStartDate).toISOString();
      request.beforeExamEndDate = moment(this.params.beforeExamEndDate).toISOString();
    }

    // イベント後受検が設定されている場合
    if (this.params.afterExam) {
      request.afterExam = this.params.afterExam;
      request.afterExamStartDate = moment(this.params.afterExamStartDate).toISOString();
      request.afterExamEndDate = moment(this.params.afterExamEndDate).toISOString();
    }

    await jgrowEventService.createEvent(request);

    return true;
  }

  /**
   * 生徒一覧エクセルロード
   * @param excelFileData 生徒一覧ダウンロードから取得したエクセルファイル
   */
  @Action({ rawError: true })
  async loadStudents(excelFileData: FormData) {
    const result: Student[] = await studentService.loadStudentExcelFile(excelFileData);
    if (result) {
      this.mergeExamineeStudents(result);
    }
  }

  /**
   * イベント詳細取得 既存イベントの取得
   * @param eventID イベントID
   */
  @Action({ rawError: true })
  async fetchEvent( eventID: number) {
    const event = await jgrowEventService.getEvent(eventID);
    const students: Student[] = await jgrowEventService.getEventStudents(eventID);
    event.students = students;
    this.setCreatedEvent(event);
  }
}

export const jgrowEventCreateModule = getModule(JgrowEventCreateModule);
